// Hayate Street Bike: a thin sport/street motorbike in three-quarter view (docs/CAR_ART.md). Everything is painted by `custom`
// with its own wheels (`noWheels`): two tyres in line, one continuous painted fairing/tank/tail, fork, low bars, engine, exhaust.
// No rider is drawn here. Wrecked: a separate lying-down drawing (two flat wheels, darkened body between, bars sticking out).
import { K, dk, lt } from '../hd-kit';
import { PAINTS } from '../art';
import { registerCarModel, type CarModel, type Painter, type V3 } from '../car-drawn';

const WR = 6.4, WU = 9, WZ = WR, TW0 = 1.4; // wheel radius, wheel centre u, centre height, tyre half width

function wreck(P: Painter) {
  // A bike lying on its side, seen from above: both wheels flat on the ground, offset so each shows as a clear rim ellipse, joined
  // by one slanted frame line with a tank on top, the fork to the front wheel and the handlebar out to one side.
  const { fill, line } = P, N = 16;
  const ground = (u: number, v: number, z: number, r: number, n = N): V3[] => Array.from({ length: n }, (_, i) => [u + Math.cos(i / n * Math.PI * 2) * r, v + Math.sin(i / n * Math.PI * 2) * r, z] as V3);
  const up: V3 = [0, 0, 1];
  const metal = dk(K.metalLo, .3), slate = dk(K.slate, .5);
  const RU = -WU - 1, RV = -5.4, FU = WU + .5, FV = 5.4; // wheel centres
  const flat = (u: number, v: number) => {
    for (let j = 0; j < N; j++) {
      const a0 = j / N * Math.PI * 2, a1 = (j + 1) / N * Math.PI * 2, am = (a0 + a1) / 2;
      const q = (a: number, z: number): V3 => [u + Math.cos(a) * WR, v + Math.sin(a) * WR, z];
      P.face([q(a0, 0), q(a1, 0), q(a1, 3.2), q(a0, 3.2)], [Math.cos(am), Math.sin(am), 0], K.tarLo);
    }
    P.face(ground(u, v, 3.2, WR), up, K.tarLo, () => {
      fill(ground(u, v, 3.3, WR - 1.1), K.metalHi); fill(ground(u, v, 3.3, WR - 1.9), metal);
      for (let k = 0; k < 3; k++) { const a = k * Math.PI / 3 + .4, r = WR - 1.9; line([[u - Math.cos(a) * r, v - Math.sin(a) * r, 3.3], [u + Math.cos(a) * r, v + Math.sin(a) * r, 3.3]], K.metal, false); }
      fill(ground(u, v, 3.3, 1.4, 10), K.metalHi); fill(ground(u, v, 3.3, .6, 8), K.ink);
    });
  };
  // a slanted slab between two ground points, w wide, z0..z1 tall: top and the faces that can be seen
  const slab = (u0: number, v0: number, u1: number, v1: number, w: number, z0: number, z1: number, col: string, top: string) => {
    const d = Math.hypot(u1 - u0, v1 - v0), nu = -(v1 - v0) / d * w / 2, nv = (u1 - u0) / d * w / 2;
    const a: [number, number] = [u0 + nu, v0 + nv], b: [number, number] = [u1 + nu, v1 + nv], c: [number, number] = [u1 - nu, v1 - nv], e: [number, number] = [u0 - nu, v0 - nv];
    const q = (p: [number, number], z: number): V3 => [p[0], p[1], z];
    P.face([q(a, z1), q(b, z1), q(c, z1), q(e, z1)], up, top);
    for (const [p0, p1, n] of [[a, b, [nu / (w / 2), nv / (w / 2), 0]], [e, c, [-nu / (w / 2), -nv / (w / 2), 0]], [b, c, [(u1 - u0) / d, (v1 - v0) / d, 0]], [e, a, [-(u1 - u0) / d, -(v1 - v0) / d, 0]]] as [[number, number], [number, number], V3][])
      P.face([q(p0, z0), q(p1, z0), q(p1, z1), q(p0, z1)], n, col);
  };
  flat(RU, RV); flat(FU, FV);
  slab(RU + 2, RV + 1, FU - 2, FV - 1, 3.4, 1, 4, slate, lt(slate, .2)); // frame line between the hubs
  slab(-2, -.6, 6, 2.2, 4.6, 4, 6.2, dk(K.slate, .3), lt(K.slate, .05)); // tank on top, lit
  slab(RU + 1, RV + .6, RU + 5.5, RV + 2.2, 3, 4, 5.4, K.ink, K.ink); // seat over the rear wheel
  slab(FU - 1, FV - 1, FU + 2.5, FV + 3, 1.6, 1.4, 3.6, K.metalLo, K.metal); // fork on the front wheel
  slab(FU - 1.5, FV - 1.5, FU - 3, FV - 13, 1.4, 3.4, 4.6, K.metalLo, K.metalHi); // handlebar out to one side
  slab(FU - 3, FV - 13, FU - 3.2, FV - 14.4, 2, 3.2, 4.8, K.ink, K.metalLo); // grip
}

function custom(P: Painter) {
  if (P.wrecked) { wreck(P); return; }
  const { body, lampOn, fill, line } = P;
  const kaminari = body === PAINTS[6];
  const tones = [dk(body, .45), dk(body, .25), body, lt(body, .22)];
  const rank = (n: V3) => Math.max(0, tones.indexOf(P.tone(n)));
  const metal = [K.metalLo, K.metal, K.metalHi, lt(K.metalHi, .25)];
  const seat = [dk(K.slate, .6), dk(K.slate, .5), dk(K.slate, .4), K.metalLo];
  const face = P.face;

  /** a profile (u, z) extruded across v0 +- hw, painted from a four step ramp */
  const ext = (prof: [number, number][], hw: number, v0: number, ramp: string[], edge?: (i: number) => (() => void) | undefined, cap?: (s: number, v: number) => (() => void) | undefined) => {
    for (let i = 1; i < prof.length; i++) {
      const a = prof[i], b = prof[(i + 1) % prof.length], du = b[0] - a[0], dz = b[1] - a[1], l = Math.hypot(du, dz) || 1;
      const n: V3 = [dz / l, 0, -du / l];
      face([[a[0], v0 - hw, a[1]], [b[0], v0 - hw, b[1]], [b[0], v0 + hw, b[1]], [a[0], v0 + hw, a[1]]], n, n[2] > .8 ? ramp[3] : ramp[rank(n)], edge?.(i));
    }
    for (const s of [1, -1]) { const v = v0 + s * hw, n: V3 = [0, s, 0]; face(prof.map(p => [p[0], v, p[1]] as V3), n, ramp[rank(n)], cap?.(s, v)); }
  };

  const wheel = (u: number) => {
    const N = 14, TW = TW0 + .9 * P.s2; // a fatter tyre end-on
    for (let j = 0; j < N; j++) {
      const a0 = j / N * Math.PI * 2, a1 = (j + 1) / N * Math.PI * 2, am = (a0 + a1) / 2;
      const p = (a: number, v: number): V3 => [u + Math.cos(a) * WR, v, WZ + Math.sin(a) * WR];
      face([p(a0, -TW), p(a1, -TW), p(a1, TW), p(a0, TW)], [Math.cos(am), 0, Math.sin(am)], j % 2 === 0 ? K.metalLo : K.tarHi);
    }
    for (const s of [1, -1]) {
      face(P.disc(u, s * TW, WZ, WR, N), [0, s, 0], K.tarLo, () => {
        const vv = s * (TW + .15);
        fill(P.disc(u, vv, WZ, WR - 1.2, N), lt(K.metalHi, .25)); fill(P.disc(u, vv, WZ, WR - 2.2, N), K.tarLo);
        for (let k = 0; k < 3; k++) { const a = k * Math.PI / 3 + .3, r = WR - 2.4; line([[u - Math.cos(a) * r, vv, WZ - Math.sin(a) * r], [u + Math.cos(a) * r, vv, WZ + Math.sin(a) * r]], K.metal, false); }
        fill(P.disc(u, vv, WZ, 1.1, 8), K.metalHi);
      });
    }
  };

  wheel(-WU); wheel(WU);
  // swingarms, exhaust (right side), engine block with fins
  for (const s of [1, -1]) ext([[-WU, WZ - 1], [-1, 5.2], [-1, 7], [-WU, WZ + 1]], .55, s * 1.9, metal);
  ext([[-12.5, 8.2], [-3, 7.6], [1.5, 5.6], [2.5, 5.6], [2.5, 7.4], [-3, 9.4], [-12.5, 9.8]], .9, 3.2, metal, i => i === 1 ? () => fill(P.endRect(-12.5, 2.3, 4.1, 8.2, 9.8), K.ink) : undefined);
  ext([[-3.5, 4], [4.5, 4], [5, 8.5], [-3, 9.5]], 2.2, 0, metal, undefined, (_s, v) => () => {
    for (let k = 0; k < 3; k++) fill(P.sideRect(v, -2.2 + k * 2.4, -1.4 + k * 2.4, 7.6, 8.6), K.metalLo);
    fill(P.sideRect(v, -2, 3, 4.6, 5.4), K.ink);
  });
  // fork legs and mudguard
  for (const s of [1, -1]) ext([[WU - .7, WZ], [WU + .7, WZ], [7.4, 15.4], [6, 15.4]], .55, s * 1.9, metal);
  ext([[WU - 4, WZ + WR + .8], [WU + 4, WZ + WR - .6], [WU + 4.2, WZ + WR + .6], [WU - 3.8, WZ + WR + 1.9]], 1.9, 0, tones);

  // one painted body: nose fairing flowing into the tank, a dip for the seat, and a tail that rises at the back
  const BODY: [number, number][] = [[-14, 9.4], [9.5, 9], [11.4, 10.6], [11.4, 13.4], [8.6, 15.4], [4.5, 15], [-.5, 14.5], [-2.4, 13.4], [-9, 13], [-14, 14.4], [-14.6, 12]];
  const nb = BODY.length;
  const bolt = (v: number): V3[] => [[7.2, v, 14], [3.4, v, 14], [1.4, v, 11.6], [3.6, v, 11.6], [2.2, v, 9.9], [6.6, v, 12.6], [4.4, v, 12.6]];
  const k = P.s2, bw = 2.5 + 2.1 * k; // the fairing and tank swell at the ends (S/N), where a thin bike nearly vanishes
  ext(BODY, bw, 0, tones,
    i => {
      if (i === 3) return () => { // nose: headlamp at the front end
        fill(P.endRect(11.4, -(bw - .5), bw - .5, 10.6, 13.6), K.ink);
        fill(P.endRect(11.4, -(bw - .9), bw - .9, 10.9, 13.3), lampOn ? K.warm : K.metalLo); if (lampOn) { fill(P.endRect(11.4, -(bw - 1.5), bw - 1.5, 11.4, 12.9), K.warmHi); fill(P.endRect(11.4, -.9, .9, 11.8, 12.5), K.white); }
      };
      if (i === nb - 1) return () => { // tail tip: lamp and plate
        fill(P.endRect(-14.6, -(bw - .5), bw - .5, 11.2, 13.4), K.ink); fill(P.endRect(-14.6, -(bw - .9), bw - .9, 11.5, 13.1), lampOn ? K.red : K.metalLo); if (lampOn) fill(P.endRect(-14.6, -1.3, 1.3, 12, 12.7), K.pink);
        fill(P.endRect(-14.6, -2.2, 2.2, 8.8, 11), K.white); fill(P.endRect(-14.6, -1.5, 1.5, 9.4, 10.4), K.metalLo);
      };
      return undefined;
    },
    (s, v) => () => {
      const w = v + s * .1;
      fill(P.sideRect(w, -13.5, 9, 9.4, 10.3), dk(body, .3)); // darker lower edge
      line([[-13.5, w, 14.3], [-9, w, 12.8], [-2.4, w, 13.2], [-.5, w, 14.3], [4.5, w, 14.8], [8.6, w, 15.2], [11, w, 13.4]], lt(body, .55), false); // top highlight
      if (lampOn) fill(P.sideRect(w, 9.2, 11.2, 11.2, 13), K.warm); // headlamp reads from the side
      if (kaminari) { fill(bolt(w), K.amber); fill(P.sideRect(w, 3.7, 4.9, 12.6, 13.4), K.white); }
    });
  if (kaminari) fill([[6, -.9, 15.6], [6, .9, 15.6], [2, .9, 15.6], [2, -.9, 15.6]], K.amber);
  ext([[-9.5, 12.8], [-2.6, 13.5], [-2.6, 14.4], [-9.5, 14.2]], 1.9, 0, seat);

  // low handlebar just above the tank, grips, short mirror stubs
  // a low bar tucked at the fairing's width: anything wider shows at E/W as a vertical stalk (the bar's depth runs up the screen)
  const bh = 1.6 + 5 * k; // half span of the bar: wide end-on, tucked at the side
  ext([[5.6, 14.9], [7, 14.9], [7, 15.5], [5.6, 15.5]], bh, 0, seat);
  if (k > .25) for (const s of [1, -1]) ext([[5.6, 14.9], [7, 14.9], [7, 15.5], [5.6, 15.5]], .7, s * (bh + .4), seat);
  if (k > .4) for (const s of [1, -1]) ext([[5, 15.5], [6.6, 15.5], [6.6, 17.2], [5, 17.2]], .9, s * (bh + .6), metal, undefined, (_s, v) => () => fill(P.sideRect(v, 5.2, 6.4, 15.8, 16.9), K.glassHi)); // mirrors, end views only // grips (the tall mirror stubs and riser are gone: they read as a stalk at E/W)
}

export const bike: CarModel = { body: [[-15, 0], [15, 0], [15, 1], [-15, 1]], hw: 1, axles: [WU, -WU], wheelR: WR, custom, noWheels: true };
registerCarModel('bike', bike);
